import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ReactComponent as AccessibilityTriggerIcon } from "../../assets/icons/accessibility-trigger.svg";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LocalizationService } from "../../services/AppLocalizationService";
import { TENANT_ID, getCurrentLanguage } from "../../components/Utils";
import RewampAccessibilityOptionsDialog from "./dialog/RewampAccessibilityOptionsDialog";

const a11yLink = {
  color: "rgba(255, 255, 255, 0.75)",
  fontSize: "0.75rem",
  fontWeight: 500,
  cursor: "pointer",
  textDecoration: "underline",
  "&:hover": { color: "background.white" },
};

const langBtn = {
  minWidth: "auto",
  height: "24px",
  px: "8px",
  py: "2px",
  fontSize: "12px",
  fontWeight: 700,
  borderRadius: "3px",
  textTransform: "none",
  lineHeight: 1,
};

const langBtnSelected = {
  ...langBtn,
  bgcolor: "accent.butterscotch", // #F0A830
  color: "#0B2118", // Black Bean
  "&:hover": { bgcolor: "accent.butterscotch" },
};

const langBtnUnselected = {
  ...langBtn,
  bgcolor: "rgba(255, 255, 255, 0.06)",
  border: "1px solid",
  borderColor: "rgba(255, 255, 255, 0.12)",
  color: "common.white",
  fontWeight: 400,
  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.12)" },
};

const a11yTriggerBtn = {
  width: "33.6px",
  height: "24px",
  bgcolor: "rgba(255, 255, 255, 0.06)",
  border: "1px solid",
  borderColor: "rgba(255, 255, 255, 0.12)",
  borderRadius: "3px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "common.white",
  cursor: "pointer",
  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.12)" },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "accent.butterscotch",
    outlineOffset: "2px",
  },
};

export const RewampAccessibilityBar = React.memo(({
  onIncreaseFontSize = () => { },
  onDecreaseFontSize = () => { },
  onResetFontSize = () => { },
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [appLanguage, setAppLanguage] = useState(getCurrentLanguage() || "en_IN");
  const [dialogOpen, setDialogOpen] = useState(false);
  // The trigger button doubles as the popover anchor so the panel drops down
  // beneath it (works for both click and the Ctrl+F2 shortcut).
  const triggerRef = useRef(null);

  const handleLanguageChange = async (lang) => {
    await LocalizationService.changeLanguage(lang, TENANT_ID);
    setAppLanguage(lang);
  };

  // Keyboard shortcut for Ctrl+F2
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "F2") {
        e.preventDefault();
        setDialogOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <Box
        sx={{
          bgcolor: "primary.dark",
          // Header decorative border per Figma — Butterscotch (Wild Lime reserved for Open status badge per styleGuide)
          borderBottom: "1.6px solid",
          borderColor: "accent.butterscotch",
          px: { xs: 2, md: 4, lg: 9 },
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-end", md: "space-between" },
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: "center" }}>
          <Typography
            component="a"
            href="#main"
            sx={a11yLink}
            onClick={(e) => {
              e.preventDefault();
              // Prefer a page-specific skip target (e.g. the landing page marks
              // its category section so we bypass the hero banner); otherwise
              // fall back to the universal <main id="main-portal"> landmark.
              const el =
                document.querySelector("[data-skip-target]") ||
                document.getElementById("main-portal");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.focus({ preventScroll: true });
              }
            }}
          >
            {t("Skip_to_main_content")}
          </Typography>
          <Typography
            component="a"
            href="screenreaderaccess"
            sx={a11yLink}
            onClick={(e) => {
              e.preventDefault();
              navigate(`${window.contextPath}/screenreaderaccess`);
            }}
          >
            {t("ScreenReader")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button
            onClick={() => handleLanguageChange("en_IN")}
            sx={appLanguage === "en_IN" ? langBtnSelected : langBtnUnselected}
            aria-pressed={appLanguage === "en_IN"}
            aria-label={`EN — ${t("Switch to English")}`}
          >
            EN
          </Button>
          <Button
            onClick={() => handleLanguageChange("hi_IN")}
            sx={appLanguage === "hi_IN" ? langBtnSelected : langBtnUnselected}
            aria-pressed={appLanguage === "hi_IN"}
            aria-label={`\u0939\u093F — ${t("Switch to Hindi")}`}
          >
            हि
          </Button>

          <Box
            sx={{
              width: "1px",
              height: "16px",
              bgcolor: "rgba(255, 255, 255, 0.15)",
              mx: "1px",
            }}
            aria-hidden="true"
          />

          <Box
            component="button"
            ref={triggerRef}
            onClick={() => setDialogOpen(true)}
            aria-label={t("Open Accessibility Options")}
            aria-keyshortcuts="Ctrl+F2"
            aria-haspopup="dialog"
            aria-expanded={dialogOpen}
            sx={a11yTriggerBtn}
          >
            <AccessibilityTriggerIcon style={{ fontSize: "16px", width: "16px", height: "16px" }} />
          </Box>
        </Box>
      </Box>

      <RewampAccessibilityOptionsDialog
        open={dialogOpen}
        anchorEl={triggerRef.current}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
});

export default RewampAccessibilityBar;
